// src/components/Calendar.js
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Calendar.css";

const monIdx = (jsDay) => (jsDay + 6) % 7;

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const days = new Date(year, month + 1, 0).getDate();

  const lead = monIdx(first.getDay());
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));

  const totalCells = Math.ceil(cells.length / 7) * 7;
  while (cells.length < totalCells) cells.push(null);

  const weeks = [];
  for (let i = 0; i < totalCells; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function startOfWeek(date) {
  const d = new Date(date);
  const w = monIdx(d.getDay()); // Mon=0 .. Sun=6
  d.setDate(d.getDate() - w);
  d.setHours(0, 0, 0, 0);
  return d;
}

function weekForDate(date) {
  const s = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) =>
    new Date(s.getFullYear(), s.getMonth(), s.getDate() + i)
  );
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toYM(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Props:
 *  - mode?: 'month' | 'week' (default 'month')
 *  - selectedYmd?: string
 *  - marked?: Map<ymd, { count?: number, confirmed?: boolean, type?: string }>
 *  - onDaySelect?: (ymd: string) => void
 *  - onMonthChange?: (ym: string) => void
 */
export default function Calendar({
  mode = "month",
  selectedYmd,
  marked = new Map(),
  onDaySelect,
  onMonthChange,
}) {
  const { t } = useTranslation();

  const WEEKDAYS = useMemo(
    () => [
      t("calendar.weekdaysShort.mon"),
      t("calendar.weekdaysShort.tue"),
      t("calendar.weekdaysShort.wed"),
      t("calendar.weekdaysShort.thu"),
      t("calendar.weekdaysShort.fri"),
      t("calendar.weekdaysShort.sat"),
      t("calendar.weekdaysShort.sun"),
    ],
    [t]
  );

  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const weeks = useMemo(() => {
    if (mode === "week") return [weekForDate(new Date())];
    return monthMatrix(year, month);
  }, [mode, year, month]);

  const monthLabel = cursor.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  const [localSelected, setLocalSelected] = useState(
    typeof selectedYmd === "string" ? selectedYmd : toYMD(new Date())
  );

  useEffect(() => {
    if (typeof selectedYmd === "string" && selectedYmd !== localSelected) {
      setLocalSelected(selectedYmd);
    }
  }, [selectedYmd, localSelected]);

  useEffect(() => {
    if (!onMonthChange) return;

    if (mode === "month") {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      onMonthChange(toYM(new Date(y, m, 1)));
    } else {
      const s = startOfWeek(new Date());
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      const ym1 = toYM(s);
      const ym2 = toYM(e);
      onMonthChange(ym1);
      if (ym2 !== ym1) onMonthChange(ym2);
    }
  }, [cursor, mode, onMonthChange]);

  const go = (delta) => {
    if (mode === "month") {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      setCursor(new Date(y, m + delta, 1));
    }
  };

  // ✅ IMPORTANT FIX:
  // In week mode, do NOT use panel--stretch, otherwise it grows huge.
  const shellClass = [
    "panel",
    mode === "month" ? "panel--stretch" : "",
    "cal-shell",
    mode === "week" ? "cal-shell--week" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const typeLabel = (dayType) => {
    const key = String(dayType || "rest").toLowerCase();
    return t(`plan.dayType.${key}`, t("plan.dayType.rest"));
  };

  return (
    <div className={shellClass}>
      <div className={`cal-wrap ${mode === "week" ? "cal-wrap--week" : ""}`}>
        <div className="cal-header">
          {mode === "month" && (
            <button
              className="cal-nav"
              onClick={() => go(-1)}
              aria-label={t("calendar.prevMonth")}
              title={t("calendar.prevMonth")}
            >
              ‹
            </button>
          )}

          <div className="cal-title">{mode === "month" ? monthLabel : ""}</div>

          {mode === "month" && (
            <button
              className="cal-nav"
              onClick={() => go(+1)}
              aria-label={t("calendar.nextMonth")}
              title={t("calendar.nextMonth")}
            >
              ›
            </button>
          )}
        </div>

        <div className={`cal-grid ${mode === "week" ? "cal-grid--week" : ""}`}>
          {WEEKDAYS.map((d, i) => (
            <div key={i} className="cal-dow" title={d}>
              {(d || "").slice(0, 1)}
            </div>
          ))}

          {weeks.flat().map((date, i) => {
            const ymd = date ? toYMD(date) : "";
            const inMonth = mode === "week" ? true : !!date && date.getMonth() === month;
            const isSelected = ymd && localSelected === ymd;

            const mark = ymd ? marked.get(ymd) : null;
            const count = mark?.count || 0;
            const dayType = mark?.type || "rest";
            const isConfirmed = !!mark?.confirmed;
            const hasWorkouts = count > 0;

            const label = typeLabel(dayType);

            return (
              <button
                key={i}
                className={[
                  "cal-cell",
                  inMonth ? "in" : "out",
                  isSelected ? "selected" : "",
                  isConfirmed ? "confirmed" : "",
                  hasWorkouts ? "marked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                disabled={!date}
                onClick={() => {
                  if (!ymd) return;
                  setLocalSelected(ymd);
                  onDaySelect?.(ymd);
                }}
                title={ymd || ""}
                aria-label={ymd ? t("calendar.dayAria", { date: ymd, type: label }) : undefined}
              >
                {date ? (
                  <>
                    <div>{date.getDate()}</div>
                    <div className={`day-type ${dayType === "rest" ? "rest" : "workout"}`}>
                      {label}
                    </div>
                  </>
                ) : (
                  ""
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}